import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import Layout          from './components/Layout';
import { ConfirmProvider, ToastProvider, UIProvider } from './components/ui';
import { routeRegistry } from './config/routeRegistry';
import { StaffNotificationProvider } from './staff/NotificationHub';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const Cars = lazy(() => import('./pages/Cars'));
const Meets = lazy(() => import('./pages/Meets'));
const Spots = lazy(() => import('./pages/Spots'));
const Posts = lazy(() => import('./pages/Posts'));
const SystemNews = lazy(() => import('./pages/SystemNews'));
const Routes_ = lazy(() => import('./pages/Routes'));
const Friendships = lazy(() => import('./pages/Friendships'));
const BugReports = lazy(() => import('./pages/support/SupportInbox'));
const BugReportRedirect = lazy(() => import('./pages/BugReportRedirect'));
const Conversations = lazy(() => import('./pages/Conversations'));
const Warnings = lazy(() => import('./pages/Warnings'));
const SpeedLimits = lazy(() => import('./pages/SpeedLimits'));
const Announcements = lazy(() => import('./pages/Announcements'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Achievements = lazy(() => import('./pages/Achievements'));
const AchievementDefs = lazy(() => import('./pages/AchievementDefs'));
const QuestTor = lazy(() => import('./pages/QuestTor'));
const BetaSignups = lazy(() => import('./pages/BetaSignups'));
const Grid = lazy(() => import('./pages/Grid'));
const Polls = lazy(() => import('./pages/Polls'));
const Gifts = lazy(() => import('./pages/Gifts'));
const PremiumMembers = lazy(() => import('./pages/PremiumMembers'));
const MarketPricing = lazy(() => import('./pages/MarketPricing'));
const Partners = lazy(() => import('./pages/Partners'));
const StaffAccounts = lazy(() => import('./pages/StaffAccounts'));
const Referrals = lazy(() => import('./pages/Referrals'));
const UgcReports = lazy(() => import('./pages/UgcReports'));
const Animations = lazy(() => import('./pages/Animations'));
const Drops = lazy(() => import('./pages/Drops'));
const NitroShop = lazy(() => import('./pages/NitroShop'));
const NitroGrant = lazy(() => import('./pages/NitroGrant'));
const VehicleOrders = lazy(() => import('./pages/VehicleOrders'));
const FuelStationBrands = lazy(() => import('./pages/FuelStationBrands'));
const FuelPrices = lazy(() => import('./pages/FuelPrices'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const LandingContent = lazy(() => import('./pages/LandingContent'));
const BusinessAccounts = lazy(() => import('./pages/BusinessAccounts'));
const BusinessLocations = lazy(() => import('./pages/BusinessLocations'));
const BusinessAds = lazy(() => import('./pages/BusinessAds'));
const BusinessModeration = lazy(() => import('./pages/BusinessModeration'));
const BusinessCenter = lazy(() => import('./pages/BusinessCenter'));
const BusinessPricing = lazy(() => import('./pages/BusinessPricing'));
const BusinessOffers = lazy(() => import('./pages/BusinessOffers'));
const Analytics = lazy(() => import('./pages/Analytics'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const BusinessSupport = lazy(() => import('./pages/BusinessSupport'));
const ShopAdmin = lazy(() => import('./pages/ShopAdmin'));
const ShopAssortment = lazy(() => import('./pages/ShopAssortment'));
const UserInventoryAdmin = lazy(() => import('./pages/UserInventoryAdmin'));
const LegalAdmin = lazy(() => import('./pages/LegalAdmin'));
const ShopProductWizard = lazy(() => import('./pages/ShopProductWizard'));
const ShopCategories = lazy(() => import('./pages/ShopCategories'));
const CashFlow = lazy(() => import('./pages/CashFlow'));
const Seasons = lazy(() => import('./pages/Seasons'));
const SeasonWizard = lazy(() => import('./pages/SeasonWizard'));
const Economy = lazy(() => import('./pages/Economy'));
const Drive = lazy(() => import('./pages/Drive'));

const routeComponents = {
  dashboard: Dashboard, analytics: Analytics, users: Users, cars: Cars, meets: Meets, spots: Spots, posts: Posts,
  'system-news': SystemNews, routes: Routes_, friendships: Friendships, 'bug-reports': BugReports,
  conversations: Conversations, warnings: Warnings, 'speed-limits': SpeedLimits, announcements: Announcements, campaigns: Campaigns,
  achievements: Achievements, 'achievement-defs': AchievementDefs, 'quest-tor': QuestTor, beta: BetaSignups,
  grid: Grid, polls: Polls, gifts: Gifts, 'premium-members': PremiumMembers, 'market-pricing': MarketPricing,
  partners: Partners, 'staff-accounts': StaffAccounts, referrals: Referrals, 'ugc-reports': UgcReports,
  animations: Animations, drops: Drops, 'nitro-shop': NitroShop, 'nitro-grant': NitroGrant,
  'vehicle-orders': VehicleOrders, 'fuel-station-brands': FuelStationBrands, 'fuel-prices': FuelPrices,
  maintenance: Maintenance, landing: LandingContent, 'business-accounts': BusinessAccounts,
  'business-locations': BusinessLocations, 'business-ads': BusinessAds, 'business-pricing': BusinessPricing,
  'business-offers': BusinessOffers, 'business-moderation': BusinessCenter,
  'business-support': BusinessSupport, 'notification-settings': NotificationSettings,
  'shop-dashboard': ShopAdmin, 'shop-catalog': ShopAdmin, 'shop-preorders': ShopAdmin, 'shop-orders': ShopAdmin, 'shop-promotions': ShopAdmin,
  'shop-designers': ShopAdmin, 'shop-finance': ShopAdmin, 'shop-settings': ShopAdmin, 'shop-documents': LegalAdmin,
  'shop-assortment': ShopAssortment, 'user-inventory': UserInventoryAdmin,
  legal: LegalAdmin,
  'cash-flow': CashFlow,
  seasons: Seasons,
  economy: Economy,
  drive: Drive,
};

const isAuth = () => !!localStorage.getItem('adminToken');

function Protected({ children }) {
  return isAuth() ? children : <Navigate to="/login" replace />;
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <div className="cc-route-error"><div><span>BŁĄD MODUŁU</span><h1>Nie udało się wyświetlić tego ekranu</h1><p>{this.state.error.message || 'Wystąpił nieoczekiwany błąd interfejsu.'}</p><div><button className="cc-button cc-button--secondary" onClick={() => window.location.assign('/shop-assortment?tab=products')}>Wróć do produktów</button><button className="cc-button cc-button--primary" onClick={() => window.location.reload()}>Wczytaj ponownie</button></div></div></div>;
  }
}

export default function App() {
  return (
    <UIProvider><ToastProvider><ConfirmProvider><BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<div className="cc-route-loader" role="status" aria-live="polite"><span /><p>Ładowanie modułu…</p></div>}>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<Protected><StaffNotificationProvider><Layout /></StaffNotificationProvider></Protected>}>
          {routeRegistry.flatMap((route) => {
            const Component = routeComponents[route.id];
            if (!Component) return [];
            if (route.path === '/') return [<Route key={route.id} index element={<Component />} />];
            return (route.routePatterns || [route.path.slice(1)]).map((path) => <Route key={`${route.id}-${path}`} path={path} element={<Component />} />);
          })}
          <Route path="bug-report/:id" element={<BugReportRedirect />} />
          <Route path="nitro-shop" element={<Navigate to="/shop-assortment?tab=items" replace />} />
          <Route path="achievement-defs" element={<Navigate to="/economy?tab=points" replace />} />
          <Route path="quest-tor" element={<Navigate to="/economy?tab=points" replace />} />
          <Route path="drops" element={<Navigate to="/economy?tab=drops" replace />} />
          <Route path="nitro-grant" element={<Navigate to="/economy?tab=nitro" replace />} />
          <Route path="shop-products/new" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-products/:id/edit" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-items/new" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-items/:id/edit" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-categories" element={<RouteErrorBoundary><ShopCategories /></RouteErrorBoundary>} />
          <Route path="seasons/new" element={<RouteErrorBoundary><SeasonWizard /></RouteErrorBoundary>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter></ConfirmProvider></ToastProvider></UIProvider>
  );
}
